import * as React from "react";
import { MatrixParametersData, TimePowerParameters } from "pages/UploadCertificate/types";

/** Shared section type used across the matrix parameter components */
export type MatrixSection = "taMin" | "taMax" | "taRange";

/** Create an empty TimePowerParameters object with all fields as empty strings */
export const createEmptyTimePowerParameters = (zwColumnCount = 1): TimePowerParameters => ({
  taValue: "",
  effectiveImpedances: Array.from({ length: zwColumnCount }, (_, i) => i === 0),
  pMinDyn: "",
  pMaxDyn: "",
  pGrenzNegMin: "",
  pGrenzNegMax: "",
  pGrenzPosMin: "",
  pGrenzPosMax: "",
});

/**
 * Normalize a row from BE: convert old numeric effectiveImpedances to boolean checkboxes.
 * A numeric value (non-empty, non-null) becomes `true`; empty/null becomes `false`.
 */
export const normalizeRow = (row: TimePowerParameters, zwColumnCount?: number): TimePowerParameters => {
  if (row.effectiveImpedances && row.effectiveImpedances.length > 0) {
    // Convert any numeric values to booleans
    const booleanImpedances = row.effectiveImpedances.map((v) =>
      typeof v === "boolean" ? v : v != null && v !== "" && !isNaN(Number(v)) && Number(v) > 0
    );
    // First Zw column (Zw,nom) is always applied
    if (booleanImpedances.length > 0) {
      booleanImpedances[0] = true;
    }
    return { ...row, effectiveImpedances: booleanImpedances };
  }
  // Fallback: wrap old scalar into boolean array — Zw,nom (index 0) is always applied
  const cols = zwColumnCount ?? 1;
  const impedances: (string | number | boolean)[] = [true, ...Array.from({ length: cols - 1 }, () => false)];
  return { ...row, effectiveImpedances: impedances };
};

/**
 * Extract unique Zw values from old-format numeric effectiveImpedances arrays.
 * Used for backward compatibility: old data stores numeric values per-cell,
 * new format stores a single value per column with boolean flags per row.
 */
export const extractZwValuesFromLegacyData = (rows: TimePowerParameters[]): (string | number)[] => {
  // Collect all unique column-index values across rows
  const maxCols = rows.reduce((m, r) => Math.max(m, (r.effectiveImpedances ?? []).length), 0);
  const zwValues: (string | number)[] = [];
  for (let i = 0; i < maxCols; i++) {
    // Find first non-empty numeric value for this column
    let found: string | number = "";
    for (const row of rows) {
      const val = row.effectiveImpedances?.[i];
      if (val != null && typeof val !== "boolean" && val !== "" && !isNaN(Number(val)) && Number(val) > 0) {
        found = val;
        break;
      }
    }
    zwValues.push(found);
  }
  return zwValues;
};

/** Merge backend taMin/taMax/taRange into a single flat array, preserving insertion order
 *  and deduplicating by taValue. Order is significant — it reflects the user-defined
 *  row ordering from the Excel template (column B "#").
 *  taMin/taMax are computed display-only rows (shown separately in the UI),
 *  so they are NOT mixed into the taRange order. */
export const flattenRows = (
  _taMin: TimePowerParameters | null | undefined,
  _taMax: TimePowerParameters | null | undefined,
  taRange: TimePowerParameters[] | null | undefined
): TimePowerParameters[] => {
  if (!taRange) return [];
  const all = taRange.map((r) => normalizeRow(r));
  const valid = all.filter((row) => row.taValue != null && row.taValue !== "" && !isNaN(Number(row.taValue)));
  const seen = new Set<number>();
  return valid.filter((row) => {
    const ta = Number(row.taValue);
    if (seen.has(ta)) return false;
    seen.add(ta);
    return true;
  });
};

/** Compute min/max rows from the flat taRange array (for read-only display).
 * T_A column uses smallest/largest taValue.
 * All other columns use their own per-column min/max independently.
 */
export const computeMinMax = (
  taRange: TimePowerParameters[],
  defaultZwColumnCount = 1
): { computedMin: TimePowerParameters; computedMax: TimePowerParameters } => {
  const validRows = taRange.filter((row) => row.taValue != null && row.taValue !== "" && !isNaN(Number(row.taValue)));
  if (validRows.length === 0) {
    return {
      computedMin: createEmptyTimePowerParameters(defaultZwColumnCount),
      computedMax: createEmptyTimePowerParameters(defaultZwColumnCount),
    };
  }

  // Copy the entire row with the lowest/highest T_A value
  const sorted = [...validRows].sort((a, b) => Number(a.taValue) - Number(b.taValue));
  const minRow = sorted[0];
  const maxRow = sorted[sorted.length - 1];

  return {
    computedMin: { ...minRow },
    computedMax: { ...maxRow },
  };
};

// Clean __typename and reconstruct backend-expected shape from flat taRange
export const cleanMatrixParametersForSave = (data: MatrixParametersData): MatrixParametersData => {
  const cleanTimePowerParams = (params: TimePowerParameters): TimePowerParameters => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      __typename,
      effectiveImpedance: _oldScalar,
      ...cleanParams
    } = params as TimePowerParameters & {
      __typename?: string;
    };
    // Keep effectiveImpedances array; drop deprecated scalar
    return cleanParams;
  };

  const allRows = data.taRange ?? [];
  const validRows = allRows.filter((row) => row.taValue != null && row.taValue !== "" && !isNaN(Number(row.taValue)));

  if (validRows.length === 0) {
    return { taMin: undefined, taMax: undefined, taRange: [] };
  }

  const { computedMin, computedMax } = computeMinMax(validRows);
  const taMin = cleanTimePowerParams(computedMin);
  const taMax = cleanTimePowerParams(computedMax);

  // taRange contains all data rows (T_A,1 ... T_A,n); taMin/taMax are computed boundaries
  const taRange = validRows.map(cleanTimePowerParams);

  return { taMin, taMax, taRange, zwValues: data.zwValues };
};

/** Polyfill for replaceAll for older browsers/TypeScript versions */
export function replaceAll(str: string, search: string, replacement: string): string {
  return str.split(search).join(replacement);
}

/** Track trailing characters (zeros and decimal point) for decimal input display */
export const getTrailingChars = (value: string): string => {
  const commaOrDot = ".";
  const trailinZeros = value
    .split("")
    .reverse()
    .reduce(
      ([trail, onnlyTrails], curr) => {
        if (value.includes(commaOrDot) && curr === "0" && onnlyTrails) {
          return [trail + "0", true] as [string, boolean];
        }
        if (curr === commaOrDot && onnlyTrails) {
          return [trail + curr, false] as [string, boolean];
        }
        return [trail, false] as [string, boolean];
      },
      ["", true] as [string, boolean]
    );

  return trailinZeros[0].split("").reverse().join("");
};

/** Get decimal separator based on locale */
export function useGetDecimalSeparator(locale: Intl.LocalesArgument) {
  const numberWithDecimalSeparator = 1.1;
  return React.useMemo(() => numberWithDecimalSeparator.toLocaleString(locale).substring(1, 2), [locale]);
}

/** Shared decimal input validation hook used by both Type 1 and Type 2 matrix parameter forms */
export function useDecimalInputValidation(locale: Intl.LocalesArgument = "de-DE") {
  const decimalSeparator = useGetDecimalSeparator(locale);

  const validateAndParseInput = React.useCallback(
    (
      inputValue: string,
      currentValue?: number,
      allowNegative?: boolean
    ): { isValid: boolean; parsedValue?: number; trailingChars: string } => {
      if (inputValue === "") {
        return { isValid: true, parsedValue: undefined, trailingChars: "" };
      }

      const fractionLength = 3;
      const dotRegEx = allowNegative
        ? `^-?[0-9]{1,}(\\.[0-9]{0,${fractionLength}})?$`
        : `^[0-9]{1,}(\\.[0-9]{0,${fractionLength}})?$`;
      const regex = new RegExp(dotRegEx);

      const valuePreFloat = replaceAll(inputValue, decimalSeparator, ".");
      const value = valuePreFloat.startsWith(".") ? "0" + valuePreFloat : valuePreFloat;

      if (value === "" || regex.test(value) || !regex.test(`${currentValue ?? ""}`)) {
        const parsedValue = parseFloat(value);
        if (isNaN(parsedValue)) {
          return { isValid: true, parsedValue: undefined, trailingChars: "" };
        }
        if (!allowNegative && parsedValue < 0) {
          return { isValid: false, trailingChars: "" };
        }
        const trailing = getTrailingChars(value);
        return { isValid: true, parsedValue, trailingChars: trailing };
      }

      return { isValid: false, trailingChars: "" };
    },
    [decimalSeparator]
  );

  return { validateAndParseInput, decimalSeparator };
}
